"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="border-primary bg-primary/10 text-zinc-100 p-6 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <CircleCheck className="size-12 text-primary mb-2" />
        <AlertTitle className="text-2xl font-default text-primary">Inquiry Received</AlertTitle>
        <AlertDescription className="text-zinc-300 text-base">
          {successMessage}
        </AlertDescription>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another Inquiry
        </Button>
      </Alert>;
  }
  const inputStyles = "bg-input/30 border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-secondary focus-visible:border-secondary";
  const labelStyles = "text-muted-foreground";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="partySize" rules={{
        required: "Party size is required",
        min: {
          value: 1,
          message: "Minimum 1 traveler"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Party Size</FormLabel>
              <FormControl>
                <Input type="number" min="1" placeholder="2" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="travelDates" rules={{
      required: "Anticipated travel dates are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Anticipated Travel Dates</FormLabel>
            <FormControl>
              <Input placeholder="e.g., Mid-October 2024 or specific dates" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="specialRequests" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Special Requests & Interests</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your dream Colombian experience... (e.g., coffee farm tours, historical sites, specific dietary needs)" className={`min-h-[120px] resize-y ${inputStyles}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <Alert variant="destructive" className="bg-destructive/10 border-destructive/20 text-destructive-foreground">
          <CircleAlert className="size-4" />
          <AlertTitle>Submission Error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto px-8 py-6 text-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-lg shadow-primary/20 rounded-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Inquiry...
          </> : <>
            Begin Your Journey
            <Send className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function ExperienceInquiry() {
  return <section id="colombiaexperienceinquiry" className="bg-background">
      <div className="bg-card py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-secondary/5 blur-[120px] pointer-events-none rounded-lg" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-default text-foreground tracking-tight">
              Plan Your Colombia Experience
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-light leading-relaxed">
              Share your vision with our curation team. We craft bespoke itineraries that immerse you in the vibrant culture, lush landscapes, and hidden gems of Colombia.
            </p>
          </div>

          <div className="bg-background/40 border border-border/50 rounded-2xl p-6 md:p-10 shadow-2xl backdrop-blur-sm">
            <FormProvider formId="colombia_inquiry_booking_form" sectionName="ColombiaExperienceInquiry" defaultValues={{
            fullName: "",
            email: "",
            phone: "",
            partySize: "",
            travelDates: "",
            specialRequests: ""
          }} submitText="Begin Your Journey" successMessage="Thank you for your inquiry. Our curation team will review your details and contact you shortly to begin designing your bespoke Colombian experience.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}